import { CONTEST_RULES } from '@/config/app.config';
import { describeShape, findFormation, type Formation } from './formations';
import { POSITIONS, type Position, type RosterPlayer, type TeamPlayer } from '@/types';

/**
 * The rules for a legal eleven.
 *
 * The eleven is picked by formation, and the formations are exactly the
 * shapes the API's position bands allow (see `formations.ts`), so the app and
 * the server agree on what a legal eleven is.
 *
 * The bench is not shaped: the API takes any four, so the app does too.
 *
 * The server remains the authority on everything else: capacity, double entry
 * and a closed contest are decided there, and its message is what is shown.
 */

export const TEAM_SIZE = CONTEST_RULES.teamSize;
export const BENCH_SIZE = CONTEST_RULES.benchSize;


/** Provider position names, for roster rows that carry no short code. */
const POSITION_FROM_NAME: Record<string, Position> = {
  Goalkeeper: 'GK',
  Defender: 'DEF',
  Midfielder: 'MID',
  Attacker: 'FWD',
};

/** Feed position ids (Sportmonks), as a last resort. */
const POSITION_FROM_ID: Record<number, Position> = {
  24: 'GK',
  25: 'DEF',
  26: 'MID',
  27: 'FWD',
};

/** The same mapping the other way, for rows that carry only a short code. */
const POSITION_ID_OF: Record<Position, number> = { GK: 24, DEF: 25, MID: 26, FWD: 27 };

/**
 * The short position code for a roster row.
 *
 * The API sends `position` directly, but older rows and the transfer market
 * carry only a name or an id, so all three are tried before giving up.
 */
export function positionOf(player: RosterPlayer): Position | null {
  if (player.position) return player.position;
  if (player.positionName) return POSITION_FROM_NAME[player.positionName] ?? null;
  if (player.positionId != null) return POSITION_FROM_ID[player.positionId] ?? null;
  return null;
}

/**
 * A roster row as the pitch and the armband picker want it.
 *
 * Those components take `TeamPlayer` — the shape the API returns for a team
 * that exists. Converting on pick means the team being built renders through
 * exactly the same components as one already entered.
 */
export function toTeamPlayer(
  player: RosterPlayer,
  captainPlayerId: number | null,
  vicePlayerId: number | null,
): TeamPlayer {
  const position = positionOf(player) ?? 'MID';

  return {
    playerId: player.playerId,
    name: player.name,
    ...(player.displayName ? { displayName: player.displayName } : {}),
    ...(player.jerseyNumber == null ? {} : { jerseyNumber: player.jerseyNumber }),
    positionId: player.positionId ?? POSITION_ID_OF[position],
    position,
    ...(player.imagePath ? { imagePath: player.imagePath } : {}),
    teamId: player.teamId,
    ...(player.teamName ? { teamName: player.teamName } : {}),
    ...(player.teamShortCode ? { teamShortCode: player.teamShortCode } : {}),
    isCaptain: player.playerId === captainPlayerId,
    isViceCaptain: player.playerId === vicePlayerId,
    ...(player.expectedPoints == null ? {} : { expectedPoints: player.expectedPoints }),
    ...(player.previousPoints == null ? {} : { previousPoints: player.previousPoints }),
    ...(player.gameweekPoints == null ? {} : { gameweekPoints: player.gameweekPoints }),
    ...(player.form == null ? {} : { form: player.form }),
    ...(player.chanceOfPlaying == null ? {} : { chanceOfPlaying: player.chanceOfPlaying }),
    ...(player.status ? { status: player.status } : {}),
  };
}

/** How many of each position are in a selection. */
export function countByPosition(players: readonly TeamPlayer[]): Record<Position, number> {
  const counts: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };

  for (const player of players) counts[player.position] += 1;

  return counts;
}

export interface TeamValidation {
  /** True once the selection would be accepted by the API. */
  isValid: boolean;
  /** The single most useful thing to fix next, or null when valid. */
  blocker: string | null;
  /** Positions still short of the formation, with how many are missing. */
  missing: Partial<Record<Position, number>>;
}

const PLURAL: Record<Position, string> = {
  GK: 'goalkeeper',
  DEF: 'defender',
  MID: 'midfielder',
  FWD: 'forward',
};

/**
 * What still stands between this selection and a legal entry.
 *
 * Returns one blocker rather than a list: someone picking a team wants the next
 * step, not an audit.
 */
export function validateSelection(
  players: readonly TeamPlayer[],
  bench: readonly TeamPlayer[],
  formation: Formation,
  captainPlayerId: number | null,
  vicePlayerId: number | null,
): TeamValidation {
  const counts = countByPosition(players);

  const missing: Partial<Record<Position, number>> = {};
  for (const position of POSITIONS) {
    const short = formation[position] - counts[position];
    if (short > 0) missing[position] = short;
  }

  const blocker = ((): string | null => {
    // An over-full line is reported first: you cannot add to a shape that is
    // already too big somewhere, so removing is the actionable step. This is
    // reachable when editing a team the API accepted under its looser rule —
    // the demo seed's own squad has four goalkeepers.
    const over = POSITIONS.find((position) => counts[position] > formation[position]);
    if (over) {
      const excess = counts[over] - formation[over];
      return `Remove ${excess} ${PLURAL[over]}${excess === 1 ? '' : 's'} — a ${formation.id} plays ${formation[over]}`;
    }

    const firstShort = POSITIONS.find((position) => missing[position]);
    if (firstShort) {
      const short = missing[firstShort] ?? 0;
      return `Pick ${short} more ${PLURAL[firstShort]}${short === 1 ? '' : 's'}`;
    }

    if (bench.length < BENCH_SIZE) {
      const short = BENCH_SIZE - bench.length;
      return `Pick ${short} more substitute${short === 1 ? '' : 's'}`;
    }

    if (captainPlayerId === null) return 'Choose a captain';
    if (vicePlayerId === null) return 'Choose a vice-captain';
    if (captainPlayerId === vicePlayerId) return 'Captain and vice must be different players';

    return null;
  })();

  return { isValid: blocker === null, blocker, missing };
}

/**
 * The formation the eleven would be in after a lineup swap, or null when the
 * swap would leave it in a shape the app does not play.
 *
 * The API applies the same formation bands to a swap as to an entry, so a
 * swap that lands outside them would be refused — checking here lets the
 * screen say so before anyone taps.
 */
export function formationAfterSwap(
  starters: readonly TeamPlayer[],
  outPlayerId: number,
  incoming: TeamPlayer,
): Formation | null {
  const next = starters.map((player) => (player.playerId === outPlayerId ? incoming : player));
  const counts = countByPosition(next);

  if (counts.GK !== 1) return null;
  return findFormation(describeShape(counts));
}
